import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { OrganizationInvitationResource, OrganizationResource } from '@clerk/types';
import {
  FolderDto,
  GalleryDto,
  MemberDto,
  MemberRole,
  TenantDto,
  UpdateTenantBody,
  UserDto,
} from '@nusign/picflow-backend-types';
import {
  BehaviorSubject,
  Observable,
  catchError,
  firstValueFrom,
  from,
  lastValueFrom,
  map,
  of,
  retry,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { AuthService } from '../auth/services/auth.service';
import { membersCacheMaxAge } from '../consts';
import { GROWTHBOOK } from '../growthbook';
import { capitalize } from '../lib/capitalize';
import { debounce } from '../lib/debounce';
import { getTenantGroups, getUserGroups } from '../lib/get-tenant-groups';
import { randomString } from '../lib/random-string';
import { sleep } from '../lib/sleep';
import { AppConfigService } from '../providers/config.service';
import { Invitation } from '../types/invitation.type';
import { DataLoaderService } from './data-loader.service';
import { DomainInfo, DomainInfoService } from './domain-info.service';
import { GetAllService } from './get-all.service';
import { LoggedInAnalyticsService } from './loggedin-analytics.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { PermissionService } from './permission.service';
import { SocketService } from './socket.service';
import { StateService } from './state.service';
import { StorageService } from './storage.service';
import { ToastService } from './toast.service';
import { UpdateService } from './update.service';

@Injectable({
  providedIn: 'root',
})
export class TenantService {
  private growthBook = inject(GROWTHBOOK);
  private state = inject(StateService);
  private http = inject(HttpClient);
  private loader = inject(DataLoaderService);
  private observableLoader = inject(ObservableDataLoaderService);
  private config = inject(AppConfigService);
  private storage = inject(StorageService);
  private loggedInAnalytics = inject(LoggedInAnalyticsService);
  private socketService = inject(SocketService);
  private auth = inject(AuthService);
  private getAllService = inject(GetAllService);
  private router = inject(Router);
  private toasts = inject(ToastService);
  private domainInfoService = inject(DomainInfoService);
  private updateService = inject(UpdateService);
  private permissionService = inject(PermissionService);

  public activeTenant$ = new BehaviorSubject<TenantDto>(undefined);

  public tenantPlanMap: Map<string, string> = new Map();
  public DateForWorkspaceStepRemoved =
    this.config.env === 'production' ? new Date('2023-04-01').getTime() : new Date('2023-03-31').getTime();

  private socketWatcher = this.socketService.onSocketEvent.subscribe(async event => {
    if (!event) return;
    if (event.name === 'tenant.updated') {
      this.clearAsync();
      const tenant = event.payload.tenant;
      tenant['storage'] = { active: BigInt(tenant['active_storage']) };
      Object.assign(this.state.activeTenant, event.payload.tenant);
    }

    if (event.name === 'user-tenant.deleted') {
      const userId: string = event.payload.userTenant['user'];
      const tenantId: string = event.payload.userTenant['tenant'];

      const isCurrentUser = userId === this.state.user?.id;
      const isCurrentTenant = tenantId === this.state.activeTenant?.id;

      if (isCurrentUser && isCurrentTenant) {
        await sleep(1000 * 2); // sleeping to avoid race condition where backend still returns the tenant we have been removed from
        this.toasts.add('You have been removed from this workspace', 'info');
        this.router.navigate(['/a', 'workspaces']);
        return await this.loadTenants();
      }
      if (isCurrentUser) {
        return await this.loadTenants();
      }
      if (isCurrentTenant) {
        this.observableLoader.forceDataRefresh('members');
      }
    }
  });

  private clearAsync = debounce(() => {
    this.observableLoader.remove('profile');
    this.loader.removeFuzzy('billingCustomer');
    this.loader.remove('plan-name_');
    this.loader.removeFuzzy('organization');
  }, 1000 * 5);

  public async loadTenants(): Promise<TenantDto[]> {
    try {
      const tokenData = await this.auth.getTokenData();
      if (!tokenData) return;
      const resp = await firstValueFrom(this.getProfileData());
      const tenants = resp.tenants;
      tenants.forEach(tenant => {
        tenant['storage'] = { active: BigInt(tenant['active_storage']) };
      });

      this.state.tenants = tenants.filter(t => !t.deleted_at);
      const defaultTenant = this.getDefaultTenant(true);
      if (defaultTenant) await this.setActiveTenant(defaultTenant);

      return tenants;
    } catch {
      // TODO: need to find a way to logout in this case...
      // Can't include auth service because we don't want to add auth0.js to the bundle
      // Maybe we could solve this by making sure the tenant service is not included in the bundle
    }
  }

  getDefaultTenant(forAutoSelect: boolean): TenantDto {
    if (forAutoSelect) {
      if (this.state.sharedWithMeActiveTenant) return undefined;
      if (this.storage.get('activeSharedWithMeTenant') || this.storage.get('backupSharedWithMeTenantId')) return;
    }
    const storageTenantId = this.storage.get('activeTenantId') || this.storage.get('backupTenantId');
    if (storageTenantId) {
      const tenant = this.state.tenants.find(t => t.id === storageTenantId);
      if (tenant) return tenant;
    }
    if (forAutoSelect) return;
    if (this.state.tenants.length === 1) return this.state.tenants[0];
  }

  public async setActiveTenant(tenant: TenantDto) {
    this.state.sharedWithMeActiveTenant = undefined;
    this.storage.remove('activeSharedWithMeTenant');
    this.storage.remove('backupSharedWithMeTenantId');

    if (!tenant) {
      this.storage.remove('activeTenantId');
      this.storage.remove('backupTenantId');
      if (this.state.activeTenant) {
        this.loader.clear();
        this.observableLoader.clear();
        this.updateService.clear();
        this.state.activeTenantRole = undefined;
        this.permissionService.setRolePermissions(undefined);
        this.state.activeTenant = undefined;
        this.state.plan = undefined;
        this.state.plan$.next(undefined);
        this.activeTenant$.next(undefined);
      }

      this.growthBook.setAttributes({
        ...this.growthBook.getAttributes(),
        tenantId: undefined,
      });
      return;
    }
    if (this.state.activeTenant && this.state.activeTenant.id === tenant.id) {
      this.state.activeTenant = tenant; // updating reference
      return;
    }
    if (this.state.activeTenant) {
      this.loader.clear();
      this.observableLoader.clear();
      this.updateService.clear();
    }
    this.state.activeTenant = tenant;

    const [resp] = await Promise.all([
      this.getCurrentTenant(),
      firstValueFrom(this.permissionService.loadTenantBillingPermissions(tenant.id)),
    ]);

    this.state.activeTenantRole = resp.role;
    this.permissionService.setRolePermissions(resp.permissions);
    this.storage.set('activeTenantId', tenant.id, true);
    this.storage.set('backupTenantId', tenant.id);

    this.growthBook.setAttributes({
      ...this.growthBook.getAttributes(),
      tenantId: tenant.id,
      tenantRole: resp.role,
      $groups: [...getTenantGroups(tenant), ...getUserGroups(this.state.user)],
    });

    // This is temporary until all users have moved to the new feature flag system. Can be removed after asset tags is live
    if (this.storage.has('featureFlagOverrides')) {
      if (this.state.activeTenantRole === 'admin' || this.state.activeTenantRole === 'owner') {
        const overrides = this.storage.get('featureFlagOverrides');
        if (overrides.assetTags) {
          this.joinTenantGroup(tenant, 'asset-tags');
        }
      }
      this.storage.remove('featureFlagOverrides');
    }

    this.activeTenant$.next(tenant);
  }

  public async refreshTenant(): Promise<void> {
    await sleep(1000); // sleeping because the updating of storage used is async
    this.observableLoader.remove('profile');
    this.observableLoader.remove('members');
    this.observableLoader.remove('invites');
    await this.loadTenants();
    this.loggedInAnalytics.identify();
  }

  public async updateTenant(tenant: TenantDto, params: UpdateTenantBody) {
    await lastValueFrom(this.http.patch(`${this.config.apiEndpoint}/tenants/${tenant.id}`, params));
    this.observableLoader.remove('profile');
    await this.loadTenants();
  }

  public checkTenantPath(path: string) {
    return this.http.get(`${this.config.apiEndpoint}/tenants/path/${path}/validate`).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  public async updateOrganization(params: { name: string; slug?: string }) {
    const organization = await this.getOrganization();
    if (!organization) return console.warn('Organization not found');
    await organization.update(params);
    this.loader.remove('organization_' + this.state.activeTenant.id);
  }

  public inviteUser(email: string, role: 'admin' | 'member') {
    // this.observableLoader.remove('invites');
    return from(this.getOrganization()).pipe(
      switchMap(org =>
        from(org.inviteMember({ emailAddress: email, role: role === 'admin' ? 'admin' : 'org:member' }))
      ),
      tap(invite => {
        this.observableLoader.update('invites', (invites: Invitation[]) => {
          return [...(invites || []), this.mapInvite(invite)];
        });
      })
    );
  }

  private getOrgInvites(org: OrganizationResource) {
    return from(org.getInvitations({ pageSize: 500, status: ['pending'] })).pipe(
      catchError(() => of({ data: [] as OrganizationInvitationResource[] }))
    );
  }

  public getPendingInvites(): Observable<Invitation[]> {
    return this.observableLoader.get(
      'invites',
      () => {
        return from(this.getOrganization()).pipe(
          switchMap(org => this.getOrgInvites(org)),
          map(resp => {
            const invites = resp.data.filter(i => i.status === 'pending');
            return invites.map(invite => this.mapInvite(invite));
          })
        );
      },
      membersCacheMaxAge,
      false
    );
  }

  private mapInvite(invite: OrganizationInvitationResource): Invitation {
    return {
      id: invite.id,
      created_at: invite.createdAt.getTime() / 1000,
      updated_at: invite.updatedAt.getTime() / 1000,
      tenant: this.state.activeTenant.id,
      email: invite.emailAddress,
      role: invite.role === 'admin' ? 'admin' : 'member',
    };
  }

  public deleteInvite(inviteId: string) {
    return from(this.getOrganization()).pipe(
      switchMap(org => this.getOrgInvites(org)),
      switchMap(resp => {
        const invites = resp.data.filter(i => i.status === 'pending');
        const invite = invites.find(i => i.id === inviteId);
        if (!invite) {
          console.warn('invite not found', invites, inviteId);
          return of();
        }
        return invite.revoke();
      }),
      tap(() =>
        this.observableLoader.update<Invitation[]>('invites', invites => invites?.filter(i => i.id !== inviteId) || [])
      )
    );
  }

  public getMembers() {
    return this.observableLoader.get(
      'members',
      () =>
        this.getAllService.getAll<MemberDto>(`${this.config.apiEndpoint}/members`).pipe(
          map(members => {
            members.forEach(m => {
              m['name'] = m.last_name ? `${m.first_name}${m.last_name}` : m.first_name;
            });
            const myId = this.state.user.id;
            return [
              members.find(m => m.id === myId),
              ...members.filter(m => m.role === 'owner' && m.id !== myId),
              ...members.filter(m => m.role === 'admin' && m.id !== myId),
              ...members.filter(m => m.role === 'member' && m.id !== myId),
            ].filter(Boolean);
          }),
          catchError(err => {
            console.error(err);
            return of([] as MemberDto[]);
          })
        ),
      membersCacheMaxAge,
      false
    );
  }

  public async updateMember(memberId: string, params: { role: 'admin' | 'member' | 'owner' }) {
    const members = await firstValueFrom(this.getMembers());
    const member = members.find(m => m.id === memberId);

    if (!member) return;

    const organization = await this.getOrganization();
    const memberships = await organization.getMemberships({ pageSize: 500 });
    const membership = memberships.data.find(m => m.publicUserData.userId === member.sub);
    if (!membership) return;

    const role = 'org:' + params.role;
    membership.update({ role });

    this.observableLoader.update('members', (members: MemberDto[]) => {
      return members.map(m => {
        if (m.id === memberId) {
          return { ...m, role: params.role };
        }
        return m;
      });
    });
  }

  public async getOrganization(orgId = this.state.activeTenant['clerk_organization_id']) {
    return this.loader.get('organization_' + orgId, async () => {
      await this.auth.init();
      return await this.auth.clerk?.getOrganization(orgId);
    });
  }

  public async removeMember(memberId: string) {
    const [members, organization] = await Promise.all([firstValueFrom(this.getMembers()), this.getOrganization()]);
    const member = members.find(m => m.id === memberId);
    if (!member) return;
    const memberships = await organization.getMemberships({ pageSize: 500 });
    const membership = memberships.data.find(m => m.publicUserData.userId === member.sub);
    if (!membership) return;
    this.observableLoader.update('members', (members: MemberDto[]) => members?.filter(m => m.id !== memberId) || []);
    await membership.destroy();
  }

  public getProfileData() {
    return this.observableLoader.get('profile', () => {
      return this.http
        .get<{
          tenants: { data: TenantDto[] };
          user: UserDto;
        }>(`${this.config.apiEndpoint}/profile/current_user?ignore_401_error=true`)
        .pipe(
          retry({ delay: 1000, count: 10 }), // retry to avoid race condition when account is first created
          map(resp => {
            if (!resp) return;
            return {
              user: resp.user,
              tenants: resp.tenants.data,
            };
          })
        );
    });
  }

  private toSafePath(path: string) {
    return path
      .toLowerCase()
      .replace(/ /g, '-')
      .replace(/[^a-z0-9]/g, '');
  }

  private getAutoTenantPath(user: UserDto, domainInfo: DomainInfo) {
    if (domainInfo && !domainInfo.isEmailProvider) {
      if (domainInfo.organization?.name) {
        return this.toSafePath(domainInfo.organization.name).slice(0, 32);
      }
      const domain = user.email.split('@')[1];
      return domain.split('.')[0];
    }
    return this.toSafePath(user.email.split('@')[0]).slice(0, 32);
  }

  private getAutoTenantName(user: UserDto, domainInfo: DomainInfo) {
    if (domainInfo && !domainInfo.isEmailProvider) {
      if (domainInfo.organization?.name) return domainInfo.organization.name?.slice(0, 32);
      const domain = user.email.split('@')[1];
      return capitalize(domain.split('.')[0]).slice(0, 32);
    }

    const first_name = user.first_name || 'Unnamed';
    return `${first_name}'s Space`.slice(0, 32);
  }

  public async autoCreateTenant(user: UserDto, count = 0): Promise<TenantDto> {
    if (count > 5) throw new Error('Could not create tenant');

    const domain = user.email.split('@')[1];
    const domainInfo = await firstValueFrom(this.domainInfoService.getDomainInfo(domain));
    const targetPath = this.getAutoTenantPath(user, domainInfo);
    const path = count ? targetPath + `-${randomString(4)}` : targetPath;

    const name = this.getAutoTenantName(user, domainInfo);

    const pathValid = await firstValueFrom(this.checkTenantPath(path));
    if (!pathValid) return await this.autoCreateTenant(user, count + 1);

    const params = domainInfo?.organization ? { onboarding_checklist: { setWorkspaceName: true } } : {};
    const created = await this.createTenant(name, path, params);
    if (!created) return await this.autoCreateTenant(user, count + 1);

    return created;
  }

  private async getTenantFromOrgId(orgId: string): Promise<TenantDto> {
    const tenants = await this.loadTenants();
    return tenants.find(t => t['clerk_organization_id'] === orgId);
  }

  private waitTillOrgExistsInBackend(orgId: string) {
    return of(false)
      .pipe(
        tap(() => this.observableLoader.remove('profile')),
        switchMap(() => from(this.getTenantFromOrgId(orgId))),
        switchMap(tenant => (tenant ? of(tenant) : throwError(() => 'Tenant not found')))
      )
      .pipe(
        retry({ count: 30, delay: 1000 }),
        catchError(() => of(undefined))
      );
  }

  private waitTillOrgDeletedInBackend(orgId: string) {
    return of(false)
      .pipe(
        tap(() => this.observableLoader.remove('profile')),
        switchMap(() => from(this.getTenantFromOrgId(orgId))),
        switchMap(tenant => (tenant ? throwError(() => 'Tenant still found') : of(true)))
      )
      .pipe(
        retry({ count: 30, delay: 1000 }),
        catchError(() => of(false))
      );
  }

  private async createOrganization(name: string, path: string) {
    try {
      return await this.auth.clerk.createOrganization({ name, slug: path });
    } catch (err) {
      const error = err.errors[0];
      if (error.code === 'form_identifier_exists') return undefined;
      throw err;
    }
  }

  public async createTenant(
    name = 'Unnamed tenant',
    path = randomString(16).toLowerCase(),
    otherParams: Partial<UpdateTenantBody> = {}
  ): Promise<TenantDto> {
    await this.auth.init();
    const organization = await this.createOrganization(name, path);
    if (!organization) return;
    const tenant = await firstValueFrom(this.waitTillOrgExistsInBackend(organization.id));
    if (!tenant) return;
    if (Object.keys(otherParams).length) {
      await this.updateTenant(tenant, otherParams);
    }
    return tenant;
  }

  public shouldShowWorkspaceDialog(item: GalleryDto | FolderDto) {
    const tenantId = (item.tenant as TenantDto)?.id || (item.tenant as string);
    const activeTenant = this.state.activeTenant;
    if (!activeTenant) return false;
    if (activeTenant.id !== tenantId) return false;
    if (this.state.activeTenantRole === 'member') return false;
    if (!activeTenant.name) return true;
    // TODO: date needs to be updated to match the date when the workspace step will be removed from the onboarding
    if (activeTenant.created_at < this.DateForWorkspaceStepRemoved / 1000) {
      // tenants created before this date didn't have the setWorkspaceName value set in the onboarding checklist
      // tenants used to be created with this name, so we use it as a flag to show the dialog
      return activeTenant.name === 'Unnamed tenant';
    }
    return !activeTenant.onboarding_checklist.setWorkspaceName;
  }

  public async deleteTenant(tenant: TenantDto) {
    const organization = await this.getOrganization(tenant['clerk_organization_id']);
    await organization.destroy();
    await firstValueFrom(this.waitTillOrgDeletedInBackend(tenant['clerk_organization_id']));
  }

  private saveTenantgroups(tenant: TenantDto, groups: string[]) {
    const checklist = tenant.onboarding_checklist;
    const newChecklist = { ...checklist };
    Object.keys(checklist).forEach(key => {
      if (key.startsWith('ff_group_')) delete newChecklist[key];
    });
    groups.forEach(group => {
      newChecklist[`ff_group_${group}`] = true;
    });
    return this.updateTenant(tenant, { onboarding_checklist: newChecklist });
  }

  public async joinTenantGroup(tenant: TenantDto, group: string) {
    const groups = getTenantGroups(tenant);
    if (groups.includes(group)) return;
    groups.push(group);
    return this.saveTenantgroups(tenant, groups);
  }

  public async leaveTenantGroup(tenant: TenantDto, group: string) {
    const groups = getTenantGroups(tenant);
    if (!groups.includes(group)) return;
    const newGroups = groups.filter(g => g !== group);
    return this.saveTenantgroups(tenant, newGroups);
  }

  private async getCurrentTenant() {
    return await lastValueFrom(
      this.http.get<{ role: MemberRole; permissions: string[]; tenant: TenantDto }>(
        `${this.config.apiEndpoint}/profile/current_tenant`
      )
    );
  }

  public async pollForTenantChange(checkFn: (tenant: TenantDto) => boolean, tries = 0): Promise<TenantDto> {
    const { tenant } = await this.getCurrentTenant();
    if (checkFn(tenant)) return tenant;
    if (tries > 30) return;
    await sleep(1000);
    return await this.pollForTenantChange(checkFn, tries + 1);
  }
}
